import { ArrowUpRight, ArrowDownRight, Minus, CircleDashed } from 'lucide-react'
import { NO_DATA } from '@/lib/format'
import {
  verdictWording,
  formatMagnitude,
  type TrendDeltaFormat,
  type TrendDeltaVerdict,
} from '@/lib/runs/verdict-wording'

// Re-exported so every existing `from '@/components/shared/trend-delta'` import
// keeps working: the verdict TYPES and their wording now live in a pure module
// the PDF report can also import (`@/lib/runs/verdict-wording`), because
// `@react-pdf/renderer` cannot mount a lucide/Tailwind component. This file
// remains the only place a verdict is DRAWN.
export type { TrendDeltaFormat, TrendDeltaVerdict }

/**
 * TrendDelta — canonical trend/delta atom.
 *
 * CANONICAL RULE (Design Language Codex §12, user rule 2026-06-11):
 * A delta looks IDENTICAL on every surface. No `className` prop — call sites needing
 * layout (width, alignment) must wrap TrendDelta in their own <span>. Never override
 * size or color at the call site. No per-context variants.
 *
 * Icons: ArrowUpRight (up) / ArrowDownRight (down) / Minus (flat/null), all size-3.
 * Number rendered as Math.abs(value) — the icon carries direction.
 * Color: up-good → text-success, down-bad → text-destructive, flat/null → text-muted-foreground.
 * `invert` flips goodness for lower-is-better metrics (position, cost, error rate).
 *
 * Supersedes: dashboard/trend-arrow.tsx
 *
 * ─── Measurement-confidence verdict mode (`verdict` prop) ─────────────────────────
 * Purely additive four-state mode for the statistically-honest period-over-period
 * verdict (docs/tasks/measurement-confidence/{spec,design-spec}.md §7.3/§B). Same
 * fixed `[icon][primary][suffix]` shape, same size, same "no className" rule — only
 * the content mapping changes per state:
 *
 *   'real'               → arrow icon, colored per `invert`, signed delta + plausible range
 *   'noise'               → Minus, ALWAYS muted, detection floor (±Xpp) + observed raw move
 *   'insufficient_data'   → CircleDashed, ALWAYS muted, em dash + actionable remedy count
 *   'unverified'          → Minus, ALWAYS muted, signed observed delta, no suffix
 *
 * Only `real` ever renders color. `noise`, `insufficient_data` and `unverified` are all
 * legitimate answers, not bad news — they stay muted regardless of the underlying raw sign.
 *
 * `gate` on the `insufficient_data` variant distinguishes which statistical floor
 * was not met (spec.md §7.3: `m < 8` paired prompts, or `median n < 4` runs per
 * prompt). The remedy noun differs ("more tracked prompts" vs "more scans") and
 * must not be conflated — recommending a scan-cadence bump for a prompt-count
 * shortfall is a false upsell.
 *
 * `unverified` is for metrics that have NOT been run through the confidence pipeline
 * at all (measurement-confidence Part 2 — Share of Voice, a cross-brand ratio, and Avg
 * Position, a mean of ordinal ranks, are different estimands from Presence/Citation
 * Share's per-prompt proportion and were never given a fabricated interval or verdict).
 * The movement is real observed data — it is NOT deleted — but a directional colour
 * asserts a confirmed, tested change, which none of these three states are entitled
 * to claim. Never confuse `unverified` with `noise`: `noise` means a test RAN and
 * found no signal; `unverified` means no test ran at all.
 *
 * The full-sentence caption ("Real change — up 6 points since last month...")
 * belongs to the parent card/page, never to TrendDelta — this component only ever
 * renders the fixed chip.
 */

interface TrendDeltaLegacyProps {
  verdict?: undefined
  /** The delta value. Positive = up, negative = down, 0/null = flat/no data. */
  value: number | null
  /**
   * How to format the magnitude. Defaults to 'num'.
   * - 'pp'    → signed(abs) + 'pp' suffix (e.g. "0.4pp") — dominant dashboard delta unit
   * - 'pct'   → pct(abs), a whole number (e.g. "4%"; "<1%" for a real move under 1%)
   * - 'pos'   → pos(abs) (e.g. "#1.2")
   * - 'count' → integer magnitude (e.g. "3")
   * - 'num'   → abs.toFixed(1) (e.g. "3.2")
   */
  format?: TrendDeltaFormat
  /**
   * Invert up/down color semantics for lower-is-better metrics (position, cost).
   * When true: down movement is good (emerald), up movement is bad (destructive).
   * Icon still reflects raw direction; only the color flips.
   */
  invert?: boolean
  /** Optional "vs …" comparison period label shown muted after the value. */
  comparison?: string
}

interface TrendDeltaVerdictProps {
  /** Three-state measurement-confidence verdict — see module docblock. */
  verdict: TrendDeltaVerdict
}

export type TrendDeltaProps = TrendDeltaLegacyProps | TrendDeltaVerdictProps

function renderVerdictChip(
  toneClass: string,
  Icon: typeof ArrowUpRight,
  primary: string,
  suffix: string,
  ariaLabel: string
): React.ReactElement {
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-medium tabular-nums ${toneClass}`}
      aria-label={ariaLabel}
    >
      <Icon className="size-3 shrink-0" aria-hidden="true" />
      <span>{primary}</span>
      <span className="font-normal text-muted-foreground">{suffix}</span>
    </span>
  )
}

/**
 * Draws whatever `verdictWording` said. The words, the direction and the
 * colour-entitlement are decided there (one source, shared with the PDF
 * report); this function only picks the icon and Tailwind class for them.
 */
function renderVerdict(verdict: TrendDeltaVerdict): React.ReactElement {
  const { primary, suffix, ariaLabel, direction, colored } = verdictWording(verdict)

  if (!colored) {
    // `insufficient_data` is the one muted state that isn't "we measured and
    // found nothing" but "we couldn't measure" — the dashed icon says so.
    const Icon = verdict.state === 'insufficient_data' ? CircleDashed : Minus
    return renderVerdictChip('text-muted-foreground', Icon, primary, suffix, ariaLabel)
  }

  // `direction` already accounts for `invert`, so goodness is never re-derived here.
  const isGood = direction === 'up'
  const Icon = verdict.state === 'real' && verdict.delta < 0 ? ArrowDownRight : ArrowUpRight
  return renderVerdictChip(
    isGood ? 'text-success' : 'text-destructive',
    Icon,
    primary,
    suffix,
    ariaLabel
  )
}

export function TrendDelta(props: TrendDeltaProps): React.ReactElement {
  if (props.verdict) {
    return renderVerdict(props.verdict)
  }

  const { value, format = 'num', invert = false, comparison } = props

  // null → no-data dash
  if (value == null) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-medium tabular-nums text-muted-foreground">
        <Minus className="size-3 shrink-0" aria-hidden="true" />
        <span>{NO_DATA}</span>
      </span>
    )
  }

  const isUp = value > 0
  const isFlat = value === 0

  let toneClass: string
  let directionLabel: string
  let Icon: typeof ArrowUpRight

  if (isFlat) {
    toneClass = 'text-muted-foreground'
    directionLabel = 'unchanged'
    Icon = Minus
  } else if (isUp) {
    toneClass = invert ? 'text-destructive' : 'text-success'
    directionLabel = 'increased'
    Icon = ArrowUpRight
  } else {
    toneClass = invert ? 'text-success' : 'text-destructive'
    directionLabel = 'decreased'
    Icon = ArrowDownRight
  }

  // Icon carries direction; number shows magnitude (abs value)
  const abs = Math.abs(value)
  const formatted = isFlat ? formatMagnitude(0, format) : formatMagnitude(abs, format)
  const ariaLabel = `${directionLabel} ${formatted}${comparison ? ` vs ${comparison}` : ''}`

  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-medium tabular-nums ${toneClass}`}
      aria-label={ariaLabel}
    >
      <Icon className="size-3 shrink-0" aria-hidden="true" />
      <span>{formatted}</span>
      {comparison && <span className="font-normal text-muted-foreground">vs {comparison}</span>}
    </span>
  )
}
